/**
 * Permission checking utilities for the Vue frontend.
 * Pure functions — no Vue dependency. Reads from localStorage.
 *
 * Permissions map shape (from /api/auth/profile):
 *   { "sales-orders": { formGroupKey: "sales-orders", canView: true, canCreate: true, ... }, ... }
 *
 * Admin users (userType === 0) bypass all checks.
 */

const STORAGE_KEY = "userPermissions";
const USER_KEY = "userdata";

/** Save permissions map to localStorage. */
export function storePermissions(permissions) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(permissions || {}));
}

/** Retrieve permissions map from localStorage. */
export function getPermissions() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

/** Remove permissions from localStorage (call on logout). */
export function clearPermissions() {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Admin-tier check used for both permission bypass and admin-only menu visibility.
 * Returns true for legacy UserType=0 admins OR users assigned the SuperAdmin/Admin role,
 * since those roles are seeded with full access in BaselineFormPermissionSeeder.
 */
export function isAdmin() {
  try {
    const user = JSON.parse(localStorage.getItem(USER_KEY) || "{}");
    if (user.userType === 0) return true;
    const roles = Array.isArray(user.roles) ? user.roles : [];
    return roles.some((r) => {
      const n = String(r).toLowerCase();
      return n === "admin" || n === "superadmin";
    });
  } catch {
    return false;
  }
}

/** Check whether the current user has a given role (case-insensitive). */
export function hasRole(roleName) {
  try {
    const user = JSON.parse(localStorage.getItem(USER_KEY) || "{}");
    const roles = Array.isArray(user.roles) ? user.roles : [];
    return roles.some((r) => String(r).toLowerCase() === String(roleName).toLowerCase());
  } catch {
    return false;
  }
}

export function canView(formGroupKey) {
  if (isAdmin()) return true;
  return !!getPermissions()[formGroupKey]?.canView;
}

export function canCreate(formGroupKey) {
  if (isAdmin()) return true;
  return !!getPermissions()[formGroupKey]?.canCreate;
}

export function canEdit(formGroupKey) {
  if (isAdmin()) return true;
  return !!getPermissions()[formGroupKey]?.canEdit;
}

export function canDelete(formGroupKey) {
  if (isAdmin()) return true;
  return !!getPermissions()[formGroupKey]?.canDelete;
}

export function canApprove(formGroupKey) {
  if (isAdmin()) return true;
  return !!getPermissions()[formGroupKey]?.canApprove;
}

export function canExport(formGroupKey) {
  if (isAdmin()) return true;
  return !!getPermissions()[formGroupKey]?.canExport;
}
